import * as Sentry from '@sentry/nuxt'
import type { DehydratedState, VueQueryPluginOptions } from '@tanstack/vue-query'
import { QueryClient, VueQueryPlugin, dehydrate, hydrate } from '@tanstack/vue-query'
import type { MutationOptions } from '@xleddyl/types'

export default defineNuxtPlugin((nuxt) => {
   const vueQueryState = useState<DehydratedState | null>('vue-query')
   const notification = useNotification()
   const loadingIndicator = useLoadingIndicator()
   const alert = useAlert()

   const {
      $i18n: { t },
      $config: runtimeConfig,
   } = useNuxtApp()

   const queryClient = new QueryClient({
      defaultOptions: {
         queries: {
            staleTime: Number(runtimeConfig.public.queryStaleTime || 1000 * 60 * 5),
            gcTime: Number(runtimeConfig.public.queryQcTime || Infinity),
            networkMode: 'offlineFirst',
            retry: false,
            refetchOnWindowFocus: false,
         },
         mutations: {
            onMutate: async (options?: MutationOptions) => {
               if (options?.confirmation) {
                  const type = options.confirmation.type
                  const title =
                     type === 'save'
                        ? t('info.save-confirm.title')
                        : type === 'delete'
                          ? t('info.delete-confirm.title')
                          : options.confirmation.title || ''

                  const description =
                     type === 'save'
                        ? t('info.save-confirm.description')
                        : type === 'delete'
                          ? t('info.delete-confirm.description')
                          : options.confirmation.title || ''

                  const confirm = await alert.show({
                     title: title,
                     description: description,
                     type: 'confirm',
                     confirmLabel: t('common.confirm'),
                     discardLabel: t('common.discard'),
                  })
                  if (!confirm) throw new Error('cancelled')
               }

               loadingIndicator.start()
            },
            onError: (e) => {
               if (e.message !== 'cancelled') {
                  if (runtimeConfig.public.mode === 'development') {
                     console.error(e)
                  } else {
                     Sentry.captureException(e, {
                        extra: {
                           message: e.message,
                           details: e.details,
                           hint: e.hint,
                           code: e.code,
                        },
                     })
                  }

                  const details = e.details?.match(/constraint "([^"]*)"/)?.[1] || e.details
                  const error = e.message.match(/constraint "([^"]*)"/)?.[1] || e.message

                  if (!error.includes('error.api')) {
                     if (details?.includes('is still referenced')) {
                        notification?.add({
                           type: 'error',
                           title: t('error.generic'),
                           description: t('error.api.cannot-delete-reference'),
                        })
                     } else {
                        notification?.add({
                           type: 'error',
                           title: t('error.generic'),
                           description: t(
                              `error.api.postgres.${error.toLowerCase()}`,
                              {},
                              {
                                 default: `${t('error.api.generic')}: ${JSON.stringify(
                                    error,
                                    null,
                                    2
                                 )}`,
                              }
                           ),
                        })
                     }
                  } else {
                     notification?.add({
                        type: 'error',
                        title: t('error.generic'),
                        description: t(e.message || '', {}, { default: t('error.api.generic') }),
                     })
                  }
               }

               loadingIndicator.clear()
            },
            onSettled: () => {
               loadingIndicator.finish()
            },
         },
      },
   })

   const options: VueQueryPluginOptions = { queryClient }

   nuxt.vueApp.use(VueQueryPlugin, options)

   if (import.meta.server) {
      nuxt.hooks.hook('app:rendered', () => {
         vueQueryState.value = dehydrate(queryClient)
      })
   }

   if (import.meta.client) {
      hydrate(queryClient, vueQueryState.value)
   }
})
